import { FormInput } from "../../components/ui/Input/FormInput/FormInput";
import { AuthPageShell } from "./components/AuthPageShell";
import { AuthSubmitButton } from "./components/AuthSubmitButton";
import { AuthTextLink } from "./components/AuthTextLink";
import { PasswordField } from "./components/PasswordField";
import { SupplierTermsConsent } from "./components/SupplierTermsConsent";
import { AUTH_COPY, AUTH_ROUTES, FIELD_MAX_LENGTH } from "./constants/auth.constants";
import { useResetPasswordViewModel } from "./hooks/useResetPasswordViewModel";
import { resetPasswordFormRules } from "./validation/authFormRules";

/**
 * Redesigned reset-password screen, gated behind `ENABLE_AUTH_UI_REDESIGN`.
 * Same request as the legacy `PasswordReset` (`POST auth/reset-password`),
 * with a "Confirm new password" field (client-side only) and a route-state
 * read that no longer crashes when opened directly.
 */
const PasswordResetNew = () => {
  const {
    isPasswordResetRequired,
    methods,
    onEmailBlur,
    confirmPasswordRule,
    isSupplier,
    supplierTermsHtml,
    termsAccepted,
    onTermsAcceptedChange,
    termsError,
    onSubmit,
    isPending
  } = useResetPasswordViewModel();

  return (
    <AuthPageShell
      eyebrow={AUTH_COPY.resetPassword.eyebrow}
      title={AUTH_COPY.resetPassword.title}
      subtitle={AUTH_COPY.resetPassword.subtitle}
      methods={methods}
      onSubmit={onSubmit}
      footer={<AuthTextLink to={AUTH_ROUTES.login}>Back to sign in</AuthTextLink>}
    >
      <FormInput
        control={methods.control}
        name="email"
        label="Current email"
        type="email"
        placeholder="Enter email"
        maxLength={FIELD_MAX_LENGTH.email}
        disabled={isPending}
        onBlur={onEmailBlur}
        data-testid="rp-email"
        {...resetPasswordFormRules.email}
      />
      <PasswordField
        control={methods.control}
        name="oldPassword"
        label={isPasswordResetRequired ? "Temporary password" : "Existing password"}
        placeholder={
          isPasswordResetRequired ? "Enter temporary password" : "Enter existing password"
        }
        maxLength={FIELD_MAX_LENGTH.password}
        disabled={isPending}
        data-testid="rp-old-password"
        {...resetPasswordFormRules.oldPassword}
      />
      <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
        <PasswordField
          control={methods.control}
          name="newPassword"
          label="New password"
          placeholder="Enter new password"
          maxLength={FIELD_MAX_LENGTH.password}
          disabled={isPending}
          data-testid="rp-new-password"
          {...resetPasswordFormRules.newPassword}
        />
        <PasswordField
          control={methods.control}
          name="confirmPassword"
          label="Confirm new password"
          placeholder="Confirm new password"
          maxLength={FIELD_MAX_LENGTH.password}
          disabled={isPending}
          data-testid="rp-confirm-password"
          {...confirmPasswordRule}
        />
      </div>
      {isSupplier && (
        <SupplierTermsConsent
          termsHtml={supplierTermsHtml}
          checked={termsAccepted}
          onCheckedChange={onTermsAcceptedChange}
          error={termsError}
        />
      )}
      <AuthSubmitButton label={AUTH_COPY.resetPassword.submitLabel} isPending={isPending} />
    </AuthPageShell>
  );
};

export default PasswordResetNew;
